/// <reference path="../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />
import { OneDriveUploadTask, IOneDriveUploadTaskResult } from "../oneDrive/OneDriveUploadTask";
import { IAuthenticationProvider } from "../interfaces/IAuthenticationProvider";
import * as AudioBackup from "../interfaces/IAudioBackup";
import * as AudioFormat from "../interfaces/IAudioFormat";
import { CreateAndInitializeAudioFormat } from "../audioBackup/AudioFormatFactory";
import { TaskList, TaskListResult } from "./TaskList";
import { LogEvents } from "../../utils/LogEvents";
import { RecoveryKeys } from '../../frontend/common/telemetry/RecoveryKeys';
import { ITranscriptionSourceFile, ITranscriptionSourceFileProvider } from "../interfaces/ITranscriptionSourceFileProvider";
import { CreateTranscriptionSourceFileProvider } from "../factory/TranscriptionSourceFileFactory";
import { GetSupportedTranscriptionStorageType } from "../office-js/HostAppSettings";

export class AudioRecoveryTask {
    private oneDriveUploadTask: OneDriveUploadTask | null = null;
    private authProvider: IAuthenticationProvider;
    private audioBackup: AudioBackup.IAudioBackup | null = null;
    private taskList: TaskList<any> | null = null;
    private file: File | null = null;

    constructor(authProvider:IAuthenticationProvider) {
        this.authProvider = authProvider;
    }

    public getFile(): File | null {
      if (this.file != null) {
        return this.file;
      }

      return null;
    }

    public async ExecuteAudioBackupRecoveryTask(audioBackup: AudioBackup.IAudioBackup, folderName: string)
        : Promise<TaskListResult<ITranscriptionSourceFile>> {
        this.audioBackup = audioBackup;

        this.taskList = new TaskList<AudioFormat.IFormatProperties | AudioFormat.IAudioFile | IOneDriveUploadTaskResult
        | { format: AudioFormat.IAudioFormat, chunks: ArrayBuffer[]} | ITranscriptionSourceFile>(
            [
                // Fetch audio file format properties from the backup
                async() : Promise<AudioFormat.IFormatProperties> =>
                    await this.audioBackup!.FetchFormatProperties(),

                // Fetch audio chunks from the backup and the audio file format generator
                async(props: AudioFormat.IFormatProperties) : Promise<{format: AudioFormat.IAudioFormat, chunks: ArrayBuffer[]}> => {
                    LogEvents.WavFormat.Start();
                    const format: AudioFormat.IAudioFormat = CreateAndInitializeAudioFormat(props);
                    const chunks: ArrayBuffer[] = await audioBackup.FetchChunks();
                    return {format, chunks};
                },

                // Generate the audio file using the format properties and audio chunks
                async(formatAndChunks: {format: AudioFormat.IAudioFormat, chunks: ArrayBuffer[]}) : Promise<AudioFormat.IAudioFile> => {
                    const audioFile: AudioFormat.IAudioFile = await formatAndChunks.format.EncodeFileUsingChunks(TranscriptionStrings.FileNameToUploadAudioFiles, formatAndChunks.chunks);
                    LogEvents.WavFormat.End();
                    return audioFile;
                },

                // Log the duration of the audio file and upload to OneDrive
                async (audioFile: AudioFormat.IAudioFile): Promise<IOneDriveUploadTaskResult> => {
                    LogEvents.Recovery.AddData([{key: RecoveryKeys.RecoveredAudioLengthSeconds, value: audioFile.duration}]);
                    this.oneDriveUploadTask = new OneDriveUploadTask(this.authProvider, folderName, audioFile.file);
                    return this.oneDriveUploadTask.UploadFile();
                },

                // Clear the backup of all format and chunk information
                async (result: IOneDriveUploadTaskResult): Promise<IOneDriveUploadTaskResult> => {
                    await audioBackup.ClearBackup();
                    return result;
                },

                // Re-initialize the backup, so that it can remain re-usable
                async (result: IOneDriveUploadTaskResult): Promise<IOneDriveUploadTaskResult> => {
                    await audioBackup.Initialize();
                    return result;
                },

                // Fetch the SourceFile information by feeding the OneDriveItem to the transcription manager
                async (result: IOneDriveUploadTaskResult): Promise<ITranscriptionSourceFile> => {
                    const transcriptionSourceFileProvider: ITranscriptionSourceFileProvider = CreateTranscriptionSourceFileProvider({
                        storageType: GetSupportedTranscriptionStorageType(),
                        descriptor:  {
                            transcription: null,
                            audioOneDriveItem: result.oneDriveItem,
                        }
                    },
                    this.authProvider);

                    return transcriptionSourceFileProvider.GetSourceFile();
                }
            ]
        );

        const result = await this.taskList.ExecuteTaskListSequentially();
        return <TaskListResult<ITranscriptionSourceFile>>result;
    }


    public async ExecuteAudioBackupRecoveryTaskInTranscriptionRecoveryFlow(audioBackup: AudioBackup.IAudioBackup)
        : Promise<TaskListResult<File>> {
        this.audioBackup = audioBackup;

        this.taskList = new TaskList<AudioFormat.IFormatProperties | AudioFormat.IAudioFile
        | { format: AudioFormat.IAudioFormat, chunks: ArrayBuffer[]} | ITranscriptionSourceFile>(
            [
                // Fetch audio file format properties from the backup
                async() : Promise<AudioFormat.IFormatProperties> =>
                    await this.audioBackup!.FetchFormatProperties(),

                // Fetch audio chunks from the backup and the audio file format generator
                async(props: AudioFormat.IFormatProperties) : Promise<{format: AudioFormat.IAudioFormat, chunks: ArrayBuffer[]}> => {
                    LogEvents.WavFormat.Start();
                    const format: AudioFormat.IAudioFormat = CreateAndInitializeAudioFormat(props);
                    const chunks: ArrayBuffer[] = await audioBackup.FetchChunks();
                    return {format, chunks};
                },

                // Generate the audio file using the format properties and audio chunks
                async(formatAndChunks: {format: AudioFormat.IAudioFormat, chunks: ArrayBuffer[]}) : Promise<AudioFormat.IAudioFile> => {
                    const audioFile: AudioFormat.IAudioFile = await formatAndChunks.format.EncodeFileUsingChunks(TranscriptionStrings.FileNameToUploadAudioFiles, formatAndChunks.chunks);
                    LogEvents.WavFormat.End();
                    this.file = audioFile.file;
                    return audioFile;
                },
            ]
        );

        const result = await this.taskList.ExecuteTaskListSequentially();
        return <TaskListResult<File>>result;
    }

    public async Cancel() {
        if (this.taskList)
            this.taskList.Cancel();

        // Cancel any pending onedrive upload task
        if (this.oneDriveUploadTask != null)
            await this.oneDriveUploadTask.Cancel();

        return;
    }
}